import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin, Modal, Input, Button, Tabs, Tag, Divider, DatePicker, Form, Row, Col } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useT202Mode } from './hooks/useT202Mode';
import { useT202Record } from './hooks/useT202Record';
import { lookupApi } from '../../services/lookupApi';
import { SharedActionBar } from '../../components/SharedActionBar';
import { useModulePermission } from '../../hooks/usePermission';
import Tab1BasicInfo from './components/Tab1BasicInfo';
import './T202Form.css';

const T202Form: React.FC = () => {
  const navigate = useNavigate();
  const { mode, isEditing, enterAdd, enterEdit, enterBrowse } = useT202Mode();
  const {
    records,
    current,
    currentIdx,
    formData,
    loading,
    setField,
    navFirst,
    navPrev,
    navNext,
    navLast,
    startAdd,
    startEdit,
    cancel,
    save,
    doDelete,
    quickSearch,
  } = useT202Record(mode, enterBrowse);

  const { canCreate, canEdit, canDelete } = useModulePermission('pwt202f1');

  const [searchText, setSearchText] = useState('');
  const [activeTab, setActiveTab] = useState('page1');
  const [employeeName, setEmployeeName] = useState('');
  const [departmentName, setDepartmentName] = useState('');

  // Resolve employee name when code changes (BR-14)
  const resolveEmployeeName = useCallback(async (code: string) => {
    if (!code.trim()) { setEmployeeName(''); return; }
    try {
      const r = await lookupApi.getName('employee', code.trim());
      setEmployeeName(r.name || '');
    } catch { setEmployeeName(''); }
  }, []);

  // Resolve department name when code changes (BR-15)
  const resolveDepartmentName = useCallback(async (code: string) => {
    if (!code.trim()) { setDepartmentName(''); return; }
    try {
      const r = await lookupApi.getName('department', code.trim());
      setDepartmentName(r.name || '');
    } catch { setDepartmentName(''); }
  }, []);

  // Employee confirm: resolve name + auto-fill department (BR-14)
  const handleEmployeeConfirm = useCallback(async (code: string) => {
    await resolveEmployeeName(code);
    if (code.trim() && !formData.ft120_14) {
      try {
        const r = await lookupApi.getName('employee_dept', code.trim());
        if (r.name) {
          setField('ft120_14', r.code || r.name);
          resolveDepartmentName(r.code || r.name);
        }
      } catch { /* ignore */ }
    }
  }, [resolveEmployeeName, resolveDepartmentName, formData.ft120_14, setField]);

  // Resolve names when current record changes
  useEffect(() => {
    if (current) {
      resolveEmployeeName(current.ft120_13 || '');
      resolveDepartmentName(current.ft120_14 || '');
    } else {
      setEmployeeName('');
      setDepartmentName('');
    }
  }, [current, resolveEmployeeName, resolveDepartmentName]);

  const handleAdd = useCallback(() => {
    startAdd();
    enterAdd();
  }, [startAdd, enterAdd]);

  const handleEdit = useCallback(() => {
    startEdit();
    enterEdit();
  }, [startEdit, enterEdit]);

  const handleDelete = useCallback(() => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除申請單「${current.ft120_01}」？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: doDelete,
    });
  }, [current, doDelete]);

  const handleSearch = useCallback(() => {
    const kw = searchText.trim();
    quickSearch(kw || '');
  }, [searchText, quickSearch]);

  const approvalStatusTag = (status: string) => {
    if (!status) return <span style={{ color: '#999' }}>-</span>;
    const colorMap: Record<string, string> = {
      Y: 'green',
      N: 'red',
      W: 'orange',
    };
    return (
      <Tag color={colorMap[status] || 'default'}>
        {status === 'Y' ? '核准' : status === 'N' ? '退回' : status === 'W' ? '待審' : status}
      </Tag>
    );
  };

  const toDate = (val: string) => (val ? dayjs(val) : null);
  const fromDate = (d: dayjs.Dayjs | null) => (d ? d.format('YYYY-MM-DD') : '');

  const tabItems = [
    {
      key: 'page1',
      label: '公告主題&內容',
      children: (
        <Tab1BasicInfo
          formData={formData}
          isEditing={isEditing}
          setField={setField}
          employeeName={employeeName}
          departmentName={departmentName}
          onEmployeeConfirm={handleEmployeeConfirm}
          onDepartmentConfirm={resolveDepartmentName}
        />
      ),
    },
    {
      key: 'page2',
      label: '核准狀況&說明',
      children: (
        <div className="t202-approval-info">
          {current ? (
            <Form layout="vertical" style={{ paddingTop: 8 }}>
              {/* 第一層核准 */}
              <Divider orientation="left" style={{ margin: '4px 0 12px' }}>第一層核准</Divider>
              <Row gutter={16}>
                <Col xs={24} sm={6}>
                  <Form.Item label="核准狀態">
                    {approvalStatusTag(current.ft120_61)}
                  </Form.Item>
                </Col>
                <Col xs={24} sm={6}>
                  <Form.Item label="代號">
                    <Input value={current.ft120_62 || ''} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={6}>
                  <Form.Item label="核准日期">
                    <Input value={current.ft120_63 ? dayjs(current.ft120_63).format('YYYY-MM-DD') : ''} disabled />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24}>
                  <Form.Item label="核准說明">
                    <Input.TextArea value={current.ft120_64 || ''} disabled rows={3} />
                  </Form.Item>
                </Col>
              </Row>

              {/* 第二層核准 */}
              <Divider orientation="left" style={{ margin: '4px 0 12px' }}>第二層核准</Divider>
              <Row gutter={16}>
                <Col xs={24} sm={6}>
                  <Form.Item label="核准狀態">
                    {approvalStatusTag(current.ft120_66)}
                  </Form.Item>
                </Col>
              </Row>

              {/* 第三層核准 (contract: 2nd approver) */}
              <Divider orientation="left" style={{ margin: '4px 0 12px' }}>第三層核准</Divider>
              <Row gutter={16}>
                <Col xs={24} sm={6}>
                  <Form.Item label="核准狀態">
                    {approvalStatusTag(current.ft120_71)}
                  </Form.Item>
                </Col>
                <Col xs={24} sm={6}>
                  <Form.Item label="代號">
                    <Input value={current.ft120_72 || ''} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={6}>
                  <Form.Item label="核准日期">
                    <Input value={current.ft120_73 ? dayjs(current.ft120_73).format('YYYY-MM-DD') : ''} disabled />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col xs={24}>
                  <Form.Item label="核准說明">
                    <Input.TextArea value={current.ft120_74 || ''} disabled rows={3} />
                  </Form.Item>
                </Col>
              </Row>

              {/* 第四層核准 */}
              <Divider orientation="left" style={{ margin: '4px 0 12px' }}>第四層核准</Divider>
              <Row gutter={16}>
                <Col xs={24} sm={6}>
                  <Form.Item label="核准狀態">
                    {approvalStatusTag(current.ft120_76)}
                  </Form.Item>
                </Col>
              </Row>

              {/* 存檔時間 */}
              <Divider style={{ margin: '4px 0 12px' }} />
              <Row gutter={16}>
                <Col xs={24} sm={8}>
                  <Form.Item label="存檔時間">
                    <Input value={current.ft120_92 ? dayjs(current.ft120_92).format('YYYY-MM-DD HH:mm:ss') : ''} disabled />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          ) : (
            <div style={{ color: '#999', padding: 16, textAlign: 'center' }}>
              請先選取一筆記錄
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div className="t202-form">
        {/* Title */}
        <div className="t202-header">
          <span className="t202-title">公告建檔申請書 (pwt202f1)</span>
          <span className="t202-meta">
            {current?.ft120_01 || '(新增)'}
          </span>
        </div>

        {/* QuickSearch — FoxPro Pwtqseek1 */}
        <div className="t202-search-bar">
          <Input
            placeholder="輸入主題或申請單號快速查詢"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleSearch}
            disabled={isEditing}
            style={{ width: 320 }}
            allowClear
          />
          <Button
            icon={<SearchOutlined />}
            onClick={handleSearch}
            disabled={isEditing}
          >
            查詢
          </Button>
        </div>

        {/* Form-level fields: 申請單號 (left) | 申請日期 (right) — matching FoxPro positions */}
        <div className="t202-form-level" style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '4px 0 8px 0', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.65)' }}>申請單號:</span>
            <Input
              value={current ? `${current.ft120_01.substring(0, 6)}-${current.ft120_01.substring(6)}` : ''}
              disabled
              style={{ width: 140, fontWeight: 600 }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.65)' }}>申請日期:</span>
            <DatePicker
              value={toDate(formData.ft120_15)}
              onChange={d => setField('ft120_15', fromDate(d))}
              disabled={!isEditing || mode === 'edit'}
              style={{ width: 160 }}
              format="YYYY-MM-DD"
            />
          </div>
        </div>

        {/* Tabs */}
        <div className="t202-tabs-container">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            size="small"
          />
        </div>

        {/* Spacer for fixed bottom bar */}
        <div style={{ height: 64 }} />

        {/* SharedActionBar — replaces page-local ActionBar + nav bar */}
        <SharedActionBar
          mode={mode}
          isEditing={isEditing}
          hasRecord={!!current}
          currentIdx={currentIdx}
          recordCount={records.length}
          onNavFirst={navFirst}
          onNavPrev={navPrev}
          onNavNext={navNext}
          onNavLast={navLast}
          onAdd={handleAdd}
          onModify={handleEdit}
          onDelete={handleDelete}
          onSave={save}
          onCancel={cancel}
          onExit={() => navigate('/erp')}
          canCreate={canCreate}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      </div>
    </Spin>
  );
};

export default T202Form;
